'use client';

/**
 * Deck editor.
 *
 * Three-pane layout:
 *   ┌─────────┬────────────────────────┬─────────┐
 *   │ reel    │  canvas (scaled slide) │ panel   │
 *   │ 220px   │   flexes               │ 320px   │
 *   └─────────┴────────────────────────┴─────────┘
 *
 * The canvas renders the currently selected slide at a *fixed* 1920×1080
 * size (so the layout matches the printed PDF exactly) and scales it down
 * to fit the available viewport. All text is inline-editable via
 * `contentEditable`; clicking the image slot pops open the library drawer.
 *
 * State persists to the server (debounced) on every mutation.
 */

import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getAuthHeaders } from '../utils/auth';
import { configureDeckApi } from '../lib/deck/api-client';
import { createId } from '@/lib/deck/storage';
import { apiLoadDeck, apiSaveDeck, apiDeleteDeck } from '@/lib/deck/api-client';
import { TEMPLATE_REGISTRY, makeBlankSlide } from '@/lib/deck/templates';
import type { BlockSlide, BlockSlidePreset, Deck, Slide } from '@/lib/deck/types';
import type { Anchor, Block } from '@/lib/deck/blocks';
import { ANCHOR_POSITIONS } from '@/lib/deck/blocks';
import {
  duplicateSlideBlock,
  patchSlideBlock,
  removeSlideBlock,
  reorderBlockWithinAnchor,
} from '@/lib/deck/block-helpers';
import { BlockCanvas } from '@/components/deck/BlockCanvas';
import { BlockInspector } from '@/components/deck/BlockInspector';
import { ConfirmDialog } from '@/components/deck/ConfirmDialog';
import { RenderSlide } from '@/components/deck/templates';
import { SidePanel } from '@/components/deck/SidePanel';
import { captureDeckSlideFrameToPngBlob } from '@/lib/deck/capture-slide-png';

type PendingConfirm = {
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
};

const SLIDE_W = 1920;
const SLIDE_H = 1080;

/** Starting point for A+/A− when a block has no explicit `fontSize` yet */
function defaultFontSizePx(block: Block): number {
  switch (block.kind) {
    case 'eyebrow':
      return 14;
    case 'headline':
      return 56;
    case 'subheadline':
      return 30;
    case 'lede':
      return 24;
    case 'body':
      return 22;
    case 'caption':
      return 12;
    case 'accent':
      return 15;
    case 'bullets':
      return 22;
    case 'conversation':
      return 16;
    default:
      return 20;
  }
}

export function DeckEditor({ deckId: deckIdProp }: { deckId?: string }) {
  const { deckId: deckIdParam } = useParams();
  const deckId = deckIdProp ?? deckIdParam ?? '';
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    configureDeckApi(() => getAuthHeaders(getAccessTokenSilently));
  }, [getAccessTokenSilently]);
  const [deck, setDeck] = useState<Deck | null>(null);
  const [ready, setReady] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPng, setExportingPng] = useState(false);
  const [bgPanelNonce, setBgPanelNonce] = useState(0);
  const slideFrameRef = useRef<HTMLDivElement>(null);
  const [saveHint, setSaveHint] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);

  // Load the deck on mount.
  useEffect(() => {
    apiLoadDeck(deckId).then((d) => {
      setDeck(d);
      setSelectedId(d?.slides[0]?.id ?? null);
      setReady(true);
    });
  }, [deckId]);

  // Persist on every mutation (debounced 800ms to avoid hammering the server).
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!deck || !ready) return;
    setSaveHint('saving');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      apiSaveDeck(deck)
        .then(() => setSaveHint('saved'))
        .catch(() => setSaveHint('idle'));
    }, 800);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [deck, ready]);

  const selectedSlide = useMemo(
    () => (deck && selectedId ? (deck.slides.find((s) => s.id === selectedId) ?? null) : null),
    [deck, selectedId],
  );

  useEffect(() => {
    setSelectedBlockId(null);
  }, [selectedSlide?.id]);

  const updateDeck = useCallback((patch: Partial<Deck>) => {
    setDeck((d) => (d ? { ...d, ...patch } : d));
  }, []);

  const updateSlide = useCallback((next: Slide) => {
    setDeck((d) => {
      if (!d) return d;
      return { ...d, slides: d.slides.map((s) => (s.id === next.id ? next : s)) };
    });
  }, []);

  const insertSlide = useCallback(
    (preset: BlockSlidePreset) => {
      const blank = makeBlankSlide(preset);
      setDeck((d) => {
        if (!d) return d;
        const idx = selectedId
          ? d.slides.findIndex((s) => s.id === selectedId)
          : d.slides.length - 1;
        const slides = [...d.slides];
        slides.splice(idx + 1, 0, blank);
        return { ...d, slides };
      });
      setSelectedId(blank.id);
      setShowTemplatePicker(false);
    },
    [selectedId],
  );

  const duplicateSlide = useCallback(() => {
    if (!deck || !selectedSlide) return;
    const copy = JSON.parse(JSON.stringify(selectedSlide)) as Slide;
    copy.id = `${selectedSlide.id}_copy_${Date.now().toString(36)}`;
    if (copy.type === 'blocks') {
      const bs = copy as BlockSlide;
      bs.blocks = bs.blocks.map((b) => ({
        ...JSON.parse(JSON.stringify(b)),
        id: createId('blk'),
      })) as BlockSlide['blocks'];
    }
    const idx = deck.slides.findIndex((s) => s.id === selectedSlide.id);
    const slides = [...deck.slides];
    slides.splice(idx + 1, 0, copy);
    setDeck({ ...deck, slides });
    setSelectedId(copy.id);
  }, [deck, selectedSlide]);

  const removeSlide = useCallback(() => {
    if (!deck || !selectedSlide) return;
    if (deck.slides.length <= 1) {
      alert('A deck needs at least one slide.');
      return;
    }
    const idx = deck.slides.findIndex((s) => s.id === selectedSlide.id);
    const slides = deck.slides.filter((s) => s.id !== selectedSlide.id);
    setDeck({ ...deck, slides });
    setSelectedId(slides[Math.min(idx, slides.length - 1)]?.id ?? null);
  }, [deck, selectedSlide]);

  const moveSlide = useCallback((id: string, dir: -1 | 1) => {
    setDeck((d) => {
      if (!d) return d;
      const idx = d.slides.findIndex((s) => s.id === id);
      if (idx < 0) return d;
      const target = idx + dir;
      if (target < 0 || target >= d.slides.length) return d;
      const slides = [...d.slides];
      [slides[idx], slides[target]] = [slides[target], slides[idx]];
      return { ...d, slides };
    });
  }, []);

  const applyImageToSelected = useCallback(
    (path: string) => {
      if (!selectedSlide) return;
      updateSlide({
        ...selectedSlide,
        bg: { ...(selectedSlide.bg ?? {}), src: path },
      } as Slide);
    },
    [selectedSlide, updateSlide],
  );

  // ── Block action callbacks (used by BlockInspector panel) ──────────────
  const selectedBlock = useMemo(
    () =>
      selectedSlide?.type === 'blocks' && selectedBlockId
        ? (selectedSlide as BlockSlide).blocks.find((b) => b.id === selectedBlockId) ?? null
        : null,
    [selectedSlide, selectedBlockId],
  );

  const patchBlock = useCallback(
    (id: string, patch: Partial<Block>) => {
      if (!selectedSlide || selectedSlide.type !== 'blocks') return;
      updateSlide(patchSlideBlock(selectedSlide as BlockSlide, id, patch));
    },
    [selectedSlide, updateSlide],
  );

  const blockSetAnchor = useCallback(
    (anchor: Anchor) => {
      if (!selectedBlockId) return;
      const p = ANCHOR_POSITIONS[anchor];
      patchBlock(selectedBlockId, { anchor, x: p.x, y: p.y } as Partial<Block>);
    },
    [selectedBlockId, patchBlock],
  );

  const blockReorder = useCallback(
    (dir: 1 | -1) => {
      if (!selectedBlockId || !selectedSlide || selectedSlide.type !== 'blocks') return;
      updateSlide(reorderBlockWithinAnchor(selectedSlide as BlockSlide, selectedBlockId, dir));
    },
    [selectedBlockId, selectedSlide, updateSlide],
  );

  const blockAdjustWidth = useCallback(
    (delta: number) => {
      if (!selectedBlockId || !selectedBlock || !('w' in selectedBlock)) return;
      const cur = (selectedBlock as Block & { w: number }).w ?? 40;
      patchBlock(selectedBlockId, { w: Math.max(8, Math.min(100, cur + delta)) } as Partial<Block>);
    },
    [selectedBlockId, selectedBlock, patchBlock],
  );

  const blockAdjustFontSize = useCallback(
    (delta: number) => {
      if (!selectedBlockId || !selectedBlock) return;
      const b = selectedBlock as Block & { fontSize?: number };
      const cur = b.fontSize ?? defaultFontSizePx(selectedBlock);
      const isConversation = selectedBlock.kind === 'conversation';
      const min = isConversation ? 12 : 10;
      const max = isConversation ? 40 : 120;
      patchBlock(selectedBlockId, {
        fontSize: Math.max(min, Math.min(max, cur + delta)),
      } as Partial<Block>);
    },
    [selectedBlockId, selectedBlock, patchBlock],
  );

  const blockSetFontSize = useCallback(
    (px: number | null) => {
      if (!selectedBlockId || !selectedBlock) return;
      if (px === null || Number.isNaN(px)) {
        patchBlock(selectedBlockId, { fontSize: undefined } as Partial<Block>);
      } else {
        const isConversation = selectedBlock.kind === 'conversation';
        const min = isConversation ? 12 : 10;
        const max = isConversation ? 40 : 120;
        patchBlock(selectedBlockId, {
          fontSize: Math.round(Math.max(min, Math.min(max, px))),
        } as Partial<Block>);
      }
    },
    [selectedBlockId, selectedBlock, patchBlock],
  );

  const blockSetColor = useCallback(
    (color: string | null) => {
      if (!selectedBlockId || !selectedSlide || selectedSlide.type !== 'blocks') return;
      if (color === null) {
        const bs = selectedSlide as BlockSlide;
        updateSlide({
          ...bs,
          blocks: bs.blocks.map((b) => {
            if (b.id !== selectedBlockId) return b;
            const next = { ...b } as Record<string, unknown>;
            delete next.color;
            return next as unknown as Block;
          }),
        });
      } else {
        patchBlock(selectedBlockId, { color } as Partial<Block>);
      }
    },
    [selectedBlockId, selectedSlide, updateSlide, patchBlock],
  );

  const blockSetTextAlign = useCallback(
    (align: 'left' | 'center' | 'right') => {
      if (!selectedBlockId || !selectedSlide || selectedSlide.type !== 'blocks') return;
      if (align === 'left') {
        const bs = selectedSlide as BlockSlide;
        updateSlide({
          ...bs,
          blocks: bs.blocks.map((b) => {
            if (b.id !== selectedBlockId) return b;
            const next = { ...b } as Record<string, unknown>;
            delete next.textAlign;
            return next as unknown as Block;
          }),
        });
      } else {
        patchBlock(selectedBlockId, { textAlign: align } as Partial<Block>);
      }
    },
    [selectedBlockId, selectedSlide, updateSlide, patchBlock],
  );

  const blockDuplicate = useCallback(() => {
    if (!selectedBlockId || !selectedSlide || selectedSlide.type !== 'blocks') return;
    updateSlide(duplicateSlideBlock(selectedSlide as BlockSlide, selectedBlockId, createId('blk')));
    setSelectedBlockId(null);
  }, [selectedBlockId, selectedSlide, updateSlide]);

  const blockDelete = useCallback(() => {
    if (!selectedBlockId || !selectedSlide || selectedSlide.type !== 'blocks') return;
    updateSlide(removeSlideBlock(selectedSlide as BlockSlide, selectedBlockId));
    setSelectedBlockId(null);
  }, [selectedBlockId, selectedSlide, updateSlide]);

  const askConfirm = useCallback((cfg: PendingConfirm) => {
    setPendingConfirm(cfg);
  }, []);

  const confirmDeleteSlide = useCallback(() => {
    if (!deck || !selectedSlide) return;
    if (deck.slides.length <= 1) {
      alert('A deck needs at least one slide.');
      return;
    }
    const idx = deck.slides.findIndex((s) => s.id === selectedSlide.id);
    askConfirm({
      title: 'Delete this slide?',
      message: `Remove slide ${idx + 1} from "${deck.name}"? This can't be undone.`,
      confirmLabel: 'Delete slide',
      onConfirm: () => {
        removeSlide();
        setPendingConfirm(null);
      },
    });
  }, [deck, selectedSlide, askConfirm, removeSlide]);

  const confirmDeleteBlock = useCallback(() => {
    if (!selectedBlock) return;
    askConfirm({
      title: 'Delete this block?',
      message: `Remove the ${selectedBlock.kind} block from this slide? This can't be undone.`,
      confirmLabel: 'Delete block',
      onConfirm: () => {
        blockDelete();
        setPendingConfirm(null);
      },
    });
  }, [selectedBlock, askConfirm, blockDelete]);

  const openBackgroundPanel = useCallback(() => {
    setSelectedBlockId(null);
    setBgPanelNonce((n) => n + 1);
  }, []);

  const onExportPng = useCallback(async () => {
    const el = slideFrameRef.current;
    if (!el || !deck || !selectedSlide) return;
    setExportingPng(true);
    try {
      const blob = await captureDeckSlideFrameToPngBlob(el, SLIDE_W, SLIDE_H);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const base = (deck.name || 'deck').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      const idx = deck.slides.findIndex((s) => s.id === selectedSlide.id) + 1;
      a.href = url;
      a.download = `${base}-slide-${String(idx).padStart(2, '0')}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`PNG export failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setExportingPng(false);
    }
  }, [deck, selectedSlide]);

  const onExportPdf = useCallback(async () => {
    if (!deck) return;
    setExporting(true);
    try {
      const res = await fetch('/api/photoslop/deck/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deck }),
      });
      if (!res.ok) {
        const msg = await res.text();
        alert(`Export failed: ${msg.slice(0, 200)}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(deck.name || 'deck').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setExporting(false);
    }
  }, [deck]);

  const requestDeleteDeck = useCallback(() => {
    if (!deck) return;
    askConfirm({
      title: 'Delete this deck?',
      message: `"${deck.name}" and all of its slides will be removed permanently.`,
      confirmLabel: 'Delete deck',
      onConfirm: () => {
        setPendingConfirm(null);
        void apiDeleteDeck(deck.id).then(() => navigate('/decks'));
      },
    });
  }, [deck, askConfirm, navigate]);

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-400">Loading…</div>
    );
  }
  if (!deck) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-cream-alt p-10 text-center">
        <p className="font-new-spirit text-2xl text-slate-800">Deck not found</p>
        <Link to="/decks" className="text-[#00FF9F] underline">
          Back to decks
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-slate-100">
      {/* Top bar */}
      <header className="flex items-center justify-between border-b border-beige bg-white px-4 py-2">
        <div className="flex items-center gap-4">
          <Link
            to="/decks"
            className="font-dank-mono text-[11px] uppercase tracking-widest text-slate-500 hover:text-marine-green"
          >
            ← Decks
          </Link>
          <input
            value={deck.name}
            onChange={(e) => updateDeck({ name: e.target.value })}
            placeholder="Untitled deck"
            className="rounded border border-transparent bg-transparent px-2 py-1 font-new-spirit text-base font-semibold text-slate-800 hover:border-beige focus:border-marine-green focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`font-dank-mono text-[10px] uppercase tracking-widest ${
              saveHint === 'saving' ? 'text-amber-500' : 'text-slate-400'
            }`}
          >
            {saveHint === 'saving' ? 'saving…' : saveHint === 'saved' ? 'saved' : ''}
          </span>
          <Link
            to={`/decks/${deck.id}/view`}
            target="_blank"
            className="rounded-full border border-beige px-3 py-1.5 font-inter text-xs text-slate-600 hover:border-marine-green"
          >
            Preview
          </Link>
          <button
            type="button"
            onClick={onExportPng}
            disabled={exportingPng || !selectedSlide}
            className="rounded-full border border-beige px-3 py-1.5 font-inter text-xs font-semibold text-slate-700 hover:border-marine-green disabled:opacity-60"
          >
            {exportingPng ? 'PNG…' : 'Download PNG'}
          </button>
          <button
            type="button"
            onClick={onExportPdf}
            disabled={exporting}
            className="rounded-full bg-marine-green px-4 py-1.5 font-inter text-xs font-semibold text-white hover:bg-marine-green/90 disabled:opacity-60"
          >
            {exporting ? 'Exporting…' : 'Download PDF'}
          </button>
          <button
            type="button"
            onClick={requestDeleteDeck}
            className="font-inter text-xs text-slate-400 hover:text-red-500"
          >
            Delete deck
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left — slide reel */}
        <aside className="flex w-[220px] flex-shrink-0 flex-col border-r border-beige bg-white">
          <div className="flex items-center justify-between border-b border-beige px-3 py-2">
            <p className="font-dank-mono text-[10px] uppercase tracking-widest text-slate-400">
              Slides · {deck.slides.length}
            </p>
            <button
              type="button"
              onClick={() => setShowTemplatePicker(true)}
              className="rounded-full bg-marine-green px-2.5 py-0.5 font-inter text-[11px] font-semibold text-white hover:bg-marine-green/90"
            >
              + Add
            </button>
          </div>
          <ol className="flex-1 space-y-2 overflow-y-auto p-3">
            {deck.slides.map((s, i) => {
              const isSelected = s.id === selectedId;
              return (
                <li key={s.id}>
                  <div
                    className={`group relative rounded-lg border ${
                      isSelected
                        ? 'border-marine-green ring-2 ring-marine-green/30'
                        : 'border-beige'
                    } bg-white transition`}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedId(s.id)}
                      className="block w-full"
                    >
                      <SlideThumb slide={s} />
                      <div className="px-2 py-1.5 text-left">
                        <p className="font-dank-mono text-[9px] uppercase tracking-widest text-slate-400">
                          {i + 1} ·{' '}
                          {s.type === 'blocks' ? ((s as BlockSlide).preset ?? 'blocks') : s.type}
                        </p>
                        <p className="truncate font-inter text-[11px] font-semibold text-slate-700">
                          {getSlideTitle(s) || '(untitled)'}
                        </p>
                      </div>
                    </button>
                    <div className="absolute right-1 top-1 hidden gap-0.5 rounded-md bg-white/90 px-1 py-0.5 shadow-sm group-hover:flex">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSlide(s.id, -1);
                        }}
                        className="px-1 font-dank-mono text-[10px] text-slate-500 hover:text-marine-green"
                        aria-label="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSlide(s.id, 1);
                        }}
                        className="px-1 font-dank-mono text-[10px] text-slate-500 hover:text-marine-green"
                        aria-label="Move down"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </aside>

        {/* Center — canvas */}
        <section className="relative flex flex-1 items-center justify-center overflow-hidden bg-slate-200 p-6">
          {selectedSlide ? (
            <ScaledCanvas slideFrameRef={slideFrameRef}>
              {selectedSlide.type === 'blocks' ? (
                <BlockCanvas
                  slide={selectedSlide}
                  onChange={updateSlide}
                  selectedId={selectedBlockId}
                  onSelectId={setSelectedBlockId}
                  onChangeBackground={openBackgroundPanel}
                />
              ) : (
                <RenderSlide
                  slide={selectedSlide}
                  onChange={updateSlide}
                  onImageClick={openBackgroundPanel}
                />
              )}
            </ScaledCanvas>
          ) : (
            <p className="text-slate-400">No slide selected.</p>
          )}
        </section>

        {/* Right — structural panel */}
        <aside className="flex w-[340px] flex-shrink-0 flex-col border-l border-beige bg-slate-50">
          {selectedSlide?.type === 'blocks' ? (
            <BlockInspector
              slide={selectedSlide}
              onChange={updateSlide}
              onPickBackground={applyImageToSelected}
              onDuplicateSlide={duplicateSlide}
              onDeleteSlide={confirmDeleteSlide}
              selectedBlock={selectedBlock}
              onSetAnchor={blockSetAnchor}
              onReorderUp={() => blockReorder(-1)}
              onReorderDown={() => blockReorder(1)}
              onAdjustWidth={blockAdjustWidth}
              onAdjustFontSize={blockAdjustFontSize}
              onSetFontSize={blockSetFontSize}
              onColor={blockSetColor}
              onSetTextAlign={blockSetTextAlign}
              onDuplicateBlock={blockDuplicate}
              onDeleteBlock={confirmDeleteBlock}
              onDeselectBlock={() => setSelectedBlockId(null)}
              openBackgroundNonce={bgPanelNonce}
            />
          ) : (
            <SidePanel
              slide={selectedSlide}
              onChange={updateSlide}
              onPickBackground={applyImageToSelected}
              onDeleteSlide={confirmDeleteSlide}
              onDuplicateSlide={duplicateSlide}
              openBackgroundNonce={bgPanelNonce}
            />
          )}
        </aside>
      </div>

      {/* Template picker */}
      {showTemplatePicker && (
        <TemplatePickerModal onClose={() => setShowTemplatePicker(false)} onPick={insertSlide} />
      )}

      <ConfirmDialog
        open={pendingConfirm != null}
        title={pendingConfirm?.title ?? ''}
        message={pendingConfirm?.message ?? ''}
        confirmLabel={pendingConfirm?.confirmLabel}
        variant="danger"
        onConfirm={() => pendingConfirm?.onConfirm()}
        onCancel={() => setPendingConfirm(null)}
      />
    </div>
  );
}

// ── Scaled canvas ──────────────────────────────────────────────────────────
/**
 * Renders its child slide inside the canonical 1920×1080 (16:9) frame, then
 * uniformly scales the frame to fit the available area.
 *
 * Important: `transform: scale` does NOT change the element's layout size,
 * which previously broke the parent's flex centering when the unscaled 1920px
 * frame overflowed the canvas pane (the slide drifted off-centre and clipped
 * on one side). We fix this by setting the WRAPPER's width/height to the
 * post-scale dimensions and using `transform-origin: top left` so the scaled
 * frame fills the wrapper exactly. The wrapper then centres normally.
 */
function ScaledCanvas({
  children,
  slideFrameRef,
}: {
  children: React.ReactNode;
  slideFrameRef: React.RefObject<HTMLDivElement | null>;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const pad = 64; // breathing room + space for the 16:9 chip on top
      const w = Math.max(0, el.clientWidth - pad);
      const h = Math.max(0, el.clientHeight - pad);
      setScale(Math.min(w / SLIDE_W, h / SLIDE_H, 1));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scaledW = SLIDE_W * scale;
  const scaledH = SLIDE_H * scale;

  return (
    <div ref={wrapRef} className="relative flex h-full w-full items-center justify-center">
      <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-full bg-white/90 px-3 py-1 font-dank-mono text-[10px] uppercase tracking-widest text-slate-500 shadow-sm">
        16:9 · 1920×1080
      </div>
      {/*
        Outer box has the *visible* dimensions so the parent's
        items-center / justify-center centres the scaled slide correctly.
      */}
      <div
        style={{
          width: `${scaledW}px`,
          height: `${scaledH}px`,
          boxShadow: '0 30px 60px -20px rgba(15, 23, 42, 0.35)',
          outline: '1px solid rgba(15, 23, 42, 0.18)',
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: 'white',
        }}
      >
        <div
          ref={slideFrameRef}
          style={{
            width: `${SLIDE_W}px`,
            height: `${SLIDE_H}px`,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
          className="deck-fixed-frame"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Thumbnails ─────────────────────────────────────────────────────────────
// Reel thumb fills the 220px aside (minus p-3 padding and 1px border each side = 194px).
const REEL_THUMB_W = 194;
const REEL_THUMB_SCALE = REEL_THUMB_W / SLIDE_W;
const REEL_THUMB_H = Math.round(SLIDE_H * REEL_THUMB_SCALE);

/**
 * Full-fidelity slide thumbnail: renders the slide at 1920×1080 and scales
 * it down via CSS transform so text, scrims, and blocks all appear correctly.
 * Pointer events are disabled so every click bubbles to the parent button.
 */
function SlideThumb({ slide }: { slide: Slide }) {
  return (
    <div
      style={{ width: REEL_THUMB_W, height: REEL_THUMB_H }}
      className="relative overflow-hidden rounded-t-lg bg-[#0D0D14]"
    >
      <div
        style={{
          width: `${SLIDE_W}px`,
          height: `${SLIDE_H}px`,
          transform: `scale(${REEL_THUMB_SCALE})`,
          transformOrigin: 'top left',
          pointerEvents: 'none',
        }}
        className="deck-fixed-frame"
      >
        <RenderSlide slide={slide} />
      </div>
    </div>
  );
}

function getSlideTitle(slide: Slide): string {
  switch (slide.type) {
    case 'blocks': {
      const b = (slide as BlockSlide).blocks.find((x) => x.kind === 'headline');
      if (b && 'text' in b) return b.text;
      const first = (slide as BlockSlide).blocks.find((x) => 'text' in x);
      return first && 'text' in first ? first.text : '';
    }
    case 'cover':
    case 'right-column':
    case 'cards':
    case 'qr':
    case 'problems':
    case 'pricing':
    case 'two-column':
    case 'ask':
    case 'transcript':
      return slide.headline;
    default:
      return '';
  }
}

// ── Add-slide modal with live thumbnails ───────────────────────────────────
const THUMB_W = 320;
const THUMB_SCALE = THUMB_W / SLIDE_W;
const THUMB_H = SLIDE_H * THUMB_SCALE;

function TemplatePickerModal({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (id: BlockSlidePreset) => void;
}) {
  // Build each preset's slide once per modal open so the thumbnails are
  // stable while the user scrolls and don't re-render on every hover.
  const previews = useMemo(
    () =>
      TEMPLATE_REGISTRY.map((t) => ({
        meta: t,
        slide: t.blank() as Slide,
      })),
    [],
  );

  // Group by category preserving registry order; default category = "Standard".
  const grouped = useMemo(() => {
    const map = new Map<string, typeof previews>();
    for (const item of previews) {
      const cat = item.meta.category ?? 'Standard';
      const arr = map.get(cat) ?? [];
      arr.push(item);
      map.set(cat, arr);
    }
    return Array.from(map.entries());
  }, [previews]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/55 p-6">
      <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-beige px-6 py-4">
          <div>
            <p className="font-dank-mono text-[10px] uppercase tracking-widest text-slate-400">
              Pick a layout
            </p>
            <h2 className="font-new-spirit text-xl font-semibold text-slate-800">Add a slide</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="font-dank-mono text-xs text-slate-400 hover:text-slate-800"
          >
            ESC
          </button>
        </header>

        <div className="overflow-y-auto px-6 py-5">
          {grouped.map(([category, items]) => (
            <section key={category} className="mb-8 last:mb-0">
              {grouped.length > 1 && (
                <p className="mb-3 font-dank-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  {category}
                </p>
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {items.map(({ meta, slide }) => (
                  <button
                    key={meta.id}
                    type="button"
                    onClick={() => onPick(meta.id)}
                    className="group flex flex-col overflow-hidden rounded-xl border border-beige bg-cream-alt text-left transition hover:border-marine-green hover:bg-white hover:shadow-md"
                  >
                    <TemplateThumb slide={slide} />
                    <div className="flex flex-col gap-1 p-3">
                      <div className="flex items-center justify-between">
                        <p className="font-new-spirit text-base font-semibold text-slate-800">
                          {meta.label}
                        </p>
                        {meta.source && (
                          <span className="font-dank-mono text-[9px] uppercase tracking-widest text-slate-400">
                            {meta.source}
                          </span>
                        )}
                      </div>
                      <p className="font-inter text-xs leading-snug text-slate-500">
                        {meta.description}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Renders a slide at canonical 1920×1080 then scales it down to a small
 * 16:9 thumbnail. Pointer events are disabled inside so the thumb behaves
 * like an image — every click bubbles to the parent button.
 */
function TemplateThumb({ slide }: { slide: Slide }) {
  return (
    <div
      style={{ width: THUMB_W, height: THUMB_H }}
      className="relative overflow-hidden rounded-t-xl bg-[#0D0D14]"
    >
      <div
        style={{
          width: `${SLIDE_W}px`,
          height: `${SLIDE_H}px`,
          transform: `scale(${THUMB_SCALE})`,
          transformOrigin: 'top left',
          pointerEvents: 'none',
        }}
        className="deck-fixed-frame"
      >
        <RenderSlide slide={slide} />
      </div>
    </div>
  );
}
